import { useState } from "react";
import { useTranslation } from "react-i18next";
import { User, MoreHorizontal, Calendar, Plus } from "lucide-react";
import { SkillBadge } from "@/components/SkillBadge";
import { MatchScore } from "@/components/MatchScore";
import { Link } from "react-router-dom";
import { KanbanCandidate } from "./types/index";
import { Badge } from "@/components/ui/badge";

interface KanbanColumnProps {
	id: string;
	title: string;
	candidates: KanbanCandidate[];
	onDragStart: (e: React.DragEvent, candidateId: string, sourceColumnId: string) => void;
	onDragOver: (e: React.DragEvent) => void;
	onDrop: (e: React.DragEvent, targetColumnId: string) => void;
}

export const KanbanColumn = ({
	id,
	title,
	candidates,
	onDragStart,
	onDragOver,
	onDrop
}: KanbanColumnProps) => {
	const { t } = useTranslation();
	const [collapsed, setCollapsed] = useState(title.toLowerCase() === 'refused');
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
		onDragOver(e);
	};

	const handleDragLeave = () => {
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		setIsDragOver(false);
		onDrop(e, id);
	};

	// Helper to get the two shortest tags
	const getShortestTags = (tags: string[]) => {
		if (!tags || tags.length === 0) return [];
		const sorted = [...tags].sort((a, b) => a.length - b.length);
		return sorted.slice(0, 2);
	};
	const getMoreCount = (tags: string[]) => Math.max(0, tags.length - 2);

	return (
		<div 
			key={id}
			className={`flex-shrink-0 flex flex-col rounded-xl border transition-all duration-300 ${
				collapsed ? 'w-10 items-center min-w-[2.5rem] max-w-[2.5rem]' : 'w-72'
			}`}
			style={{
				background: collapsed ? '#f8fafc' : '#fff',
				borderColor: '#e5e7eb',
				...(isDragOver ? { outline: '2px solid #3b82f6', outlineOffset: 2, background: collapsed ? undefined : 'rgba(59,130,246,0.08)' } : {})
			}}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{collapsed ? (
				<div className="flex flex-col items-center w-full pt-2 cursor-pointer select-none" onClick={() => setCollapsed(false)}>
					<span className="font-medium text-foreground text-xs font-semibold" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>{title}</span>
					<button
						className="mt-2 text-xs text-blue-500 hover:underline focus:outline-none"
						onClick={e => { e.stopPropagation(); setCollapsed(false); }}
						aria-label={t('common.expand')}
					>
						+
					</button>
					{candidates.length > 0 && (
						<div className="mt-2">
							<div className={`rounded-full flex items-center justify-center font-bold shadow transition-all duration-300 h-6 w-6 text-[10px] ${
								isDragOver ? 'bg-blue-500' : 'bg-blue-600'
							} text-white`}>
								{candidates.length}
							</div>
						</div>
					)}
				</div>
			) : (
				<div className={`px-4 py-3 border-b bg-white/80 dark:bg-zinc-900/80 rounded-t-xl ${
					isDragOver ? 'bg-blue-50/40 dark:bg-blue-900/30' : ''
				}`} style={{borderBottom: '2px solid #e5e7eb', boxShadow: '0 2px 4px 0 rgba(0,0,0,0.03)'}}>
					<div className="flex justify-between items-center">
						<h3 className="font-semibold text-lg text-foreground tracking-tight">{title}</h3>
						<div className="flex items-center gap-2">
							<Badge variant="secondary" className={`text-xs ${
								isDragOver ? 'bg-blue-100 dark:bg-blue-900' : 'bg-white/40 dark:bg-white/10'
							}`}>{candidates.length}</Badge>
							<button
								className="ml-2 text-xs text-blue-500 hover:underline focus:outline-none"
								onClick={() => setCollapsed((c) => !c)}
								aria-label={t('common.collapse')}
							>
								–
							</button>
						</div>
					</div>
				</div>
			)}
			{!collapsed ? (
				<div className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px] ${
					isDragOver ? 'bg-blue-50/20 dark:bg-blue-900/20' : ''
				}`}>
					{candidates.map(candidate => {
						const company = candidate.company;
						const dateStr = candidate.date || candidate.createdAt;
						let daysAgo = null;
						if (dateStr) {
							const dateObj = new Date(dateStr);
							const now = new Date();
							const diffTime = now.getTime() - dateObj.getTime();
							const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
							daysAgo = diffDays;
						}
						return (
							<div 
								key={candidate.id}
								className="bg-white/80 dark:bg-black/60 backdrop-blur-sm rounded-lg border border-gray-200 dark:border-zinc-700 shadow-sm p-3 cursor-move hover:shadow-md transition-all duration-300"
								draggable
								onDragStart={(e) => onDragStart(e, candidate.id, id)}
							>
								<div className="flex justify-between items-start">
									<div>
										<Link to={`/matches/${candidate.id}`} className="font-medium hover:underline">
											{candidate.name}
										</Link>
										{company && (
											<div className="text-sm text-gray-600 mt-1">
												{company}
											</div>
										)}
										{dateStr && (
											<div className="mt-2 flex items-center text-xs text-gray-500">
												<Calendar className="h-3 w-3 mr-1" />
												{new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
												{daysAgo !== null && <span className="ml-2">• {daysAgo === 0 ? 'today' : daysAgo === 1 ? '1 day ago' : `${daysAgo} days ago`}</span>}
											</div>
										)}
									</div>
									{candidate.score !== undefined && !candidate.analysisInProgress && (
										<MatchScore score={candidate.score} size="md" />
									)}
									{candidate.analysisInProgress && (
										<MatchScore score={candidate.score} running={true} size="md" />
									)}
								</div>
							</div>
						);
					})}
					{candidates.length === 0 && (
						<div className="h-20 flex items-center justify-center text-sm text-foreground/40 italic">
							{t('kanban.noOffers')}
						</div>
					)}
				</div>
			) : null}
		</div>
	);
};
